import React, { useState, useEffect } from 'react';
import { AthleteProfile } from '../types';
import { triggerHaptic } from '../utils/haptics';
import { 
  Trophy, 
  Flame, 
  Sparkles, 
  RotateCcw, 
  X, 
  Target, 
  Play, 
  CheckCircle2, 
  Zap, 
  Dumbbell, 
  ArrowRight,
  ShieldCheck,
  Award
} from 'lucide-react';

interface MiniGamesModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AthleteProfile;
  onEarnXpAndStats?: (xp: number, gameName: string, statsToAdd?: { pts?: number; makes?: number }) => void;
}

type GameMode = 'horse' | 'aroundTheWorld';

interface ShotSpot {
  id: string;
  name: string;
  difficulty: number; // percentage difficulty 0-100
  xp: number;
  icon: string;
}

const AROUND_THE_WORLD_SPOTS: ShotSpot[] = [
  { id: 'spot_1', name: 'Right Corner 3PT', difficulty: 55, xp: 20, icon: '📐' },
  { id: 'spot_2', name: 'Right Wing 3PT', difficulty: 50, xp: 25, icon: '🏀' },
  { id: 'spot_3', name: 'Top of Key 3PT', difficulty: 45, xp: 30, icon: '🎯' },
  { id: 'spot_4', name: 'Left Wing 3PT', difficulty: 50, xp: 25, icon: '🏀' },
  { id: 'spot_5', name: 'Left Corner 3PT', difficulty: 55, xp: 20, icon: '📐' },
  { id: 'spot_6', name: 'High Post Free Throw', difficulty: 30, xp: 35, icon: '⚡' },
  { id: 'spot_7', name: 'Rim Layup Swish', difficulty: 15, xp: 45, icon: '🔥' },
];

const HORSE_SHOT_TYPES = [
  { id: 'free_throw', name: 'Clean Free Throw', difficulty: 35, xp: 20, tag: 'Standard' },
  { id: 'corner_three', name: 'Corner 3-Pointer', difficulty: 55, xp: 35, tag: 'Distance' },
  { id: 'off_glass', name: 'Bank Shot off Glass', difficulty: 45, xp: 30, tag: 'Precision' },
  { id: 'sky_hook', name: 'Kareem Sky Hook', difficulty: 65, xp: 50, tag: 'Classic' },
  { id: 'behind_backboard', name: 'Behind Backboard Rainbow', difficulty: 80, xp: 75, tag: 'Trickshot' },
  { id: 'half_court', name: 'Half-Court Hail Mary', difficulty: 88, xp: 100, tag: 'Legendary' },
];

export const MiniGamesModal: React.FC<MiniGamesModalProps> = ({
  isOpen,
  onClose,
  user,
  onEarnXpAndStats,
}) => {
  const [activeGame, setActiveGame] = useState<GameMode>('aroundTheWorld');

  // Around the World Game State
  const [atwCurrentSpotIndex, setAtwCurrentSpotIndex] = useState(0);
  const [atwAttempts, setAtwAttempts] = useState(0);
  const [atwMakes, setAtwMakes] = useState(0);
  const [atwStreak, setAtwStreak] = useState(0);
  const [atwMeterPosition, setAtwMeterPosition] = useState(0);
  const [atwMeterDirection, setAtwMeterDirection] = useState<1 | -1>(1);
  const [atwIsMeterActive, setAtwIsMeterActive] = useState(false);
  const [atwLastResult, setAtwLastResult] = useState<{ status: 'swish' | 'miss' | 'rim' | null; msg: string }>({
    status: null,
    msg: '',
  });
  const [atwIsGameOver, setAtwIsGameOver] = useState(false);

  // H-O-R-S-E Game State
  const [horsePlayerLetters, setHorsePlayerLetters] = useState<string[]>([]);
  const [horseCpuLetters, setHorseCpuLetters] = useState<string[]>([]);
  const [horseTurn, setHorseTurn] = useState<'player' | 'cpu'>('player');
  const [horseSelectedShot, setHorseSelectedShot] = useState(HORSE_SHOT_TYPES[0]);
  const [horseLastAction, setHorseLastAction] = useState<string>('Select a shot and release your shot meter!');
  const [horseIsGameOver, setHorseIsGameOver] = useState(false);
  const [horseWinner, setHorseWinner] = useState<'player' | 'cpu' | null>(null);

  // Meter Animation Loop for Around the World & H-O-R-S-E
  useEffect(() => {
    if (!atwIsMeterActive) return;

    const interval = setInterval(() => {
      setAtwMeterPosition((prev) => {
        let next = prev + atwMeterDirection * 4;
        if (next >= 100) {
          next = 100;
          setAtwMeterDirection(-1);
        } else if (next <= 0) {
          next = 0;
          setAtwMeterDirection(1);
        }
        return next;
      });
    }, 20);

    return () => clearInterval(interval);
  }, [atwIsMeterActive, atwMeterDirection]);

  if (!isOpen) return null;

  // Around the World: Start Meter Shot
  const handleStartAtwMeter = () => {
    setAtwMeterPosition(0);
    setAtwMeterDirection(1);
    setAtwIsMeterActive(true);
    setAtwLastResult({ status: null, msg: '' });
  };

  // Around the World: Release Shot Meter
  const handleReleaseAtwShot = () => {
    if (!atwIsMeterActive) return;
    setAtwIsMeterActive(false);
    setAtwAttempts((prev) => prev + 1);

    const currentSpot = AROUND_THE_WORLD_SPOTS[atwCurrentSpotIndex];
    // Target green zone is between 42 and 58
    const greenMin = 42;
    const greenMax = 58;
    const isGreenSwish = atwMeterPosition >= greenMin && atwMeterPosition <= greenMax;
    const isClose = atwMeterPosition >= greenMin - 12 && atwMeterPosition <= greenMax + 12;

    if (isGreenSwish) {
      triggerHaptic('heavy');
      setAtwMakes((prev) => prev + 1);
      setAtwStreak((prev) => prev + 1);
      setAtwLastResult({
        status: 'swish',
        msg: `🔥 PERFECT SWISH! Cleared ${currentSpot.name}! (+${currentSpot.xp} XP)`,
      });

      // Award XP
      if (onEarnXpAndStats) {
        onEarnXpAndStats(currentSpot.xp, `Around the World - ${currentSpot.name}`, { pts: 2, makes: 1 });
      }

      // Move to next spot or win
      if (atwCurrentSpotIndex + 1 < AROUND_THE_WORLD_SPOTS.length) {
        setAtwCurrentSpotIndex((prev) => prev + 1);
      } else {
        // Complete Circuit Win!
        setAtwIsGameOver(true);
        if (onEarnXpAndStats) {
          onEarnXpAndStats(150, 'Around the World - Complete Court Clearance! 🏆', { pts: 10, makes: 7 });
        }
      }
    } else if (isClose && Math.random() > 0.4) {
      // Lucky Rim Bounce
      triggerHaptic('medium');
      setAtwMakes((prev) => prev + 1);
      setAtwStreak((prev) => prev + 1);
      setAtwLastResult({
        status: 'swish',
        msg: `🏀 Lucky Bounce in! Cleared ${currentSpot.name}! (+${currentSpot.xp} XP)`,
      });

      if (onEarnXpAndStats) {
        onEarnXpAndStats(currentSpot.xp, `Around the World - ${currentSpot.name}`, { pts: 2, makes: 1 });
      }

      if (atwCurrentSpotIndex + 1 < AROUND_THE_WORLD_SPOTS.length) {
        setAtwCurrentSpotIndex((prev) => prev + 1);
      } else {
        setAtwIsGameOver(true);
        if (onEarnXpAndStats) {
          onEarnXpAndStats(150, 'Around the World - Complete Court Clearance! 🏆', { pts: 10, makes: 7 });
        }
      }
    } else {
      triggerHaptic('light');
      setAtwStreak(0);
      setAtwLastResult({
        status: 'miss',
        msg: `❌ Shot Clanked off the back rim! Try ${currentSpot.name} again.`,
      });
    }
  };

  const handleResetAtw = () => {
    setAtwCurrentSpotIndex(0);
    setAtwAttempts(0);
    setAtwMakes(0);
    setAtwStreak(0);
    setAtwMeterPosition(0);
    setAtwIsMeterActive(false);
    setAtwIsGameOver(false);
    setAtwLastResult({ status: null, msg: '' });
  };

  // H-O-R-S-E: Player Shoot
  const handleHorsePlayerShoot = () => {
    if (!atwIsMeterActive) {
      setAtwMeterPosition(0);
      setAtwMeterDirection(1);
      setAtwIsMeterActive(true);
      return;
    }

    setAtwIsMeterActive(false);
    const shot = horseSelectedShot;
    const greenMin = 44;
    const greenMax = 56;
    const isPlayerMake = atwMeterPosition >= greenMin && atwMeterPosition <= greenMax;

    if (horseTurn === 'player') {
      if (isPlayerMake) {
        triggerHaptic('heavy');
        setHorseLastAction(`🔥 You SWISHED the ${shot.name}! CPU Rival must match!`);
        // CPU attempt to match
        const cpuDifficultyThreshold = (100 - shot.difficulty) / 100;
        const cpuRoll = Math.random();
        if (cpuRoll < cpuDifficultyThreshold) {
          setHorseLastAction((prev) => `${prev} 🏀 CPU Rival MATCHED your ${shot.name}! Control stays with you.`);
        } else {
          // CPU gets a letter
          const HORSE_WORD = ['H', 'O', 'R', 'S', 'E'];
          const nextCpuLetters = [...horseCpuLetters, HORSE_WORD[horseCpuLetters.length]];
          setHorseCpuLetters(nextCpuLetters);
          setHorseLastAction((prev) => `${prev} ❌ CPU Rival MISSED and got letter '${HORSE_WORD[nextCpuLetters.length - 1]}'!`);

          if (onEarnXpAndStats) {
            onEarnXpAndStats(shot.xp, `H-O-R-S-E Point - ${shot.name}`, { pts: 2, makes: 1 });
          }

          if (nextCpuLetters.length === 5) {
            setHorseIsGameOver(true);
            setHorseWinner('player');
            if (onEarnXpAndStats) {
              onEarnXpAndStats(200, 'H-O-R-S-E Victory over Court Rival! 👑', { pts: 10, makes: 5 });
            }
          }
        }
      } else {
        triggerHaptic('light');
        setHorseLastAction(`❌ You missed the ${shot.name}. Turn switches to CPU Rival!`);
        setHorseTurn('cpu');
        // Trigger CPU turn after short delay
        setTimeout(() => handleCpuTurn(shot), 1000);
      }
    }
  };

  const handleCpuTurn = (shot: typeof HORSE_SHOT_TYPES[0]) => {
    const cpuShot = HORSE_SHOT_TYPES[Math.floor(Math.random() * HORSE_SHOT_TYPES.length)];
    const cpuSuccessChance = (100 - cpuShot.difficulty) / 100;
    const cpuMade = Math.random() < cpuSuccessChance;

    if (cpuMade) {
      setHorseLastAction(`🤖 CPU Rival SWISHED ${cpuShot.name}! You MUST match this shot!`);
      setHorseTurn('player');
    } else {
      setHorseLastAction(`🤖 CPU Rival missed ${cpuShot.name}! Ball is back in your hands.`);
      setHorseTurn('player');
    }
  };

  const handleResetHorse = () => {
    setHorsePlayerLetters([]);
    setHorseCpuLetters([]);
    setHorseTurn('player');
    setHorseIsGameOver(false);
    setHorseWinner(null);
    setHorseLastAction('Select a shot type and release your shot meter!');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-950 border border-lime-400/40 rounded-[2.5rem] p-6 max-w-2xl w-full shadow-2xl space-y-5 text-white relative max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
              <Trophy className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div>
              <h3 className="font-black italic uppercase text-xl text-white tracking-tight flex items-center gap-2">
                <span>Playground Court Mini-Games 🎮</span>
              </h3>
              <p className="text-xs text-indigo-200/80 font-semibold">
                Test shot accuracy, complete challenges, and earn real XP & match stats!
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-indigo-300 hover:text-white rounded-xl bg-indigo-950 border border-white/10 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Game Mode Selector */}
        <div className="flex items-center space-x-2 bg-indigo-950 p-1.5 rounded-2xl border border-white/10 text-xs font-black italic uppercase">
          <button
            onClick={() => {
              setActiveGame('aroundTheWorld');
              handleResetAtw();
            }}
            className={`flex-1 py-2.5 rounded-xl transition flex items-center justify-center space-x-2 ${
              activeGame === 'aroundTheWorld'
                ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            <Target className="w-4 h-4" />
            <span>Around The World</span>
          </button>

          <button
            onClick={() => {
              setActiveGame('horse');
              handleResetHorse();
            }}
            className={`flex-1 py-2.5 rounded-xl transition flex items-center justify-center space-x-2 ${
              activeGame === 'horse'
                ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            <Flame className="w-4 h-4" />
            <span>H-O-R-S-E Rival Match</span>
          </button>
        </div>

        {/* GAME MODE 1: AROUND THE WORLD */}
        {activeGame === 'aroundTheWorld' && (
          <div className="space-y-5">
            {/* Spot Circuit Progress Bar */}
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-3">
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-indigo-200 font-bold uppercase flex items-center">
                  <Award className="w-4 h-4 mr-1 text-lime-400" />
                  Circuit Progress: Spot {atwCurrentSpotIndex + 1} of {AROUND_THE_WORLD_SPOTS.length}
                </span>
                <span className="text-lime-300 font-extrabold">
                  Makes: {atwMakes} / {atwAttempts} (Streak: {atwStreak} 🔥)
                </span>
              </div>

              {/* Spot Badges Strip */}
              <div className="grid grid-cols-7 gap-1.5">
                {AROUND_THE_WORLD_SPOTS.map((spot, idx) => {
                  const isCurrent = idx === atwCurrentSpotIndex;
                  const isCleared = idx < atwCurrentSpotIndex;

                  return (
                    <div
                      key={spot.id}
                      className={`p-2 rounded-xl text-center border transition-all ${
                        isCleared
                          ? 'bg-lime-400/20 border-lime-400 text-lime-300 font-bold'
                          : isCurrent
                          ? 'bg-lime-400 text-black font-black italic shadow-lg shadow-lime-400/30 scale-105'
                          : 'bg-indigo-900/40 border-white/5 text-indigo-300/40'
                      }`}
                    >
                      <span className="text-base block">{spot.icon}</span>
                      <span className="text-[9px] font-mono font-bold uppercase block truncate">
                        {isCleared ? '✓' : `Spot ${idx + 1}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active Spot Card & Shot Meter */}
            {!atwIsGameOver ? (
              <div className="bg-indigo-900/70 p-6 rounded-3xl border border-white/10 space-y-4 text-center">
                <div>
                  <span className="px-3 py-1 bg-lime-400/20 text-lime-300 border border-lime-400/40 rounded-full text-xs font-mono font-bold uppercase">
                    Active Shot Spot
                  </span>
                  <h4 className="text-2xl font-black italic uppercase text-white mt-1">
                    {AROUND_THE_WORLD_SPOTS[atwCurrentSpotIndex].name}
                  </h4>
                  <p className="text-xs text-indigo-200/80 font-medium mt-0.5">
                    Difficulty: {AROUND_THE_WORLD_SPOTS[atwCurrentSpotIndex].difficulty}% • Reward: +{AROUND_THE_WORLD_SPOTS[atwCurrentSpotIndex].xp} XP
                  </p>
                </div>

                {/* Shot Accuracy Meter Component */}
                <div className="space-y-2 max-w-md mx-auto">
                  <div className="flex items-center justify-between text-[10px] font-mono font-bold text-indigo-300 uppercase">
                    <span>Miss Left</span>
                    <span className="text-lime-400 font-black">GREEN SWISH ZONE (42%-58%)</span>
                    <span>Miss Right</span>
                  </div>

                  <div className="relative w-full h-8 bg-indigo-950 rounded-2xl overflow-hidden border-2 border-white/20 p-1">
                    {/* Green Swish Target Bar */}
                    <div className="absolute top-1 bottom-1 left-[42%] right-[42%] bg-lime-400 rounded-lg opacity-80 animate-pulse border border-white" />
                    
                    {/* Moving Meter Needle */}
                    <div
                      className="absolute top-0 bottom-0 w-2.5 bg-rose-500 rounded-full shadow-lg shadow-rose-500 transition-all duration-75"
                      style={{ left: `calc(${atwMeterPosition}% - 5px)` }}
                    />
                  </div>
                </div>

                {/* Result Message Feed */}
                {atwLastResult.msg && (
                  <p className={`text-xs font-black italic p-2 rounded-xl ${
                    atwLastResult.status === 'swish'
                      ? 'bg-lime-400/20 text-lime-300 border border-lime-400/40'
                      : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                  }`}>
                    {atwLastResult.msg}
                  </p>
                )}

                {/* Action Buttons */}
                <div className="pt-2 flex items-center justify-center space-x-3">
                  {!atwIsMeterActive ? (
                    <button
                      type="button"
                      onClick={handleStartAtwMeter}
                      className="px-8 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-sm uppercase rounded-2xl shadow-xl shadow-lime-400/20 transition transform active:scale-95 flex items-center space-x-2"
                    >
                      <Play className="w-5 h-5 fill-black" />
                      <span>Start Shot Meter</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleReleaseAtwShot}
                      className="px-10 py-3.5 bg-rose-500 hover:bg-rose-600 text-white font-black italic text-sm uppercase rounded-2xl shadow-xl shadow-rose-500/30 transition transform active:scale-95 animate-pulse flex items-center space-x-2"
                    >
                      <Target className="w-5 h-5" />
                      <span>RELEASE SHOT NOW!</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Victory Card */
              <div className="p-8 bg-gradient-to-br from-amber-500/20 via-indigo-950 to-indigo-900 border-2 border-amber-400 rounded-3xl text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-gradient-to-tr from-amber-400 to-yellow-300 text-black rounded-3xl flex items-center justify-center font-black text-3xl shadow-xl">
                  🏆
                </div>
                <div>
                  <h4 className="text-2xl font-black italic uppercase text-amber-300">
                    Around The World Completed!
                  </h4>
                  <p className="text-xs text-indigo-200 mt-1">
                    You cleared all 7 court spots! Earned <strong className="text-lime-400">+150 Bonus XP</strong> and logged stats to your athlete profile.
                  </p>
                </div>

                <button
                  onClick={handleResetAtw}
                  className="px-6 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-xs uppercase rounded-xl shadow-lg transition"
                >
                  Play Again 🔄
                </button>
              </div>
            )}
          </div>
        )}

        {/* GAME MODE 2: H-O-R-S-E RIVAL MATCH */}
        {activeGame === 'horse' && (
          <div className="space-y-5">
            {/* Scoreboard Box */}
            <div className="grid grid-cols-2 gap-4">
              {/* Player Box */}
              <div className="bg-indigo-950/80 p-4 rounded-2xl border border-lime-400/40 text-center space-y-1">
                <div className="flex items-center justify-center space-x-2">
                  <img src={user?.avatar || ''} alt={user?.name || 'Athlete'} className="w-6 h-6 rounded-full object-cover ring-2 ring-lime-400" />
                  <span className="text-xs font-black uppercase text-white">{user?.name || 'Athlete'}</span>
                </div>
                <div className="flex justify-center space-x-1.5 pt-1">
                  {['H', 'O', 'R', 'S', 'E'].map((char, i) => (
                    <span
                      key={i}
                      className={`w-7 h-7 rounded-xl flex items-center justify-center font-black text-xs border ${
                        i < horsePlayerLetters.length
                          ? 'bg-rose-500 text-white border-rose-400 shadow-md'
                          : 'bg-indigo-900 text-indigo-400/40 border-white/5'
                      }`}
                    >
                      {char}
                    </span>
                  ))}
                </div>
              </div>

              {/* CPU Rival Box */}
              <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 text-center space-y-1">
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-lg">🤖</span>
                  <span className="text-xs font-black uppercase text-indigo-200">Court Rival AI</span>
                </div>
                <div className="flex justify-center space-x-1.5 pt-1">
                  {['H', 'O', 'R', 'S', 'E'].map((char, i) => (
                    <span
                      key={i}
                      className={`w-7 h-7 rounded-xl flex items-center justify-center font-black text-xs border ${
                        i < horseCpuLetters.length
                          ? 'bg-rose-500 text-white border-rose-400 shadow-md'
                          : 'bg-indigo-900 text-indigo-400/40 border-white/5'
                      }`}
                    >
                      {char}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Game Feed & Shot Selector */}
            {!horseIsGameOver ? (
              <div className="bg-indigo-900/70 p-6 rounded-3xl border border-white/10 space-y-4">
                <div className="text-center space-y-1">
                  <span className="px-3 py-1 bg-lime-400/20 text-lime-300 border border-lime-400/30 text-[10px] font-mono font-bold uppercase rounded-full">
                    Turn: {horseTurn === 'player' ? `${user?.name || 'Player'} (Attacker)` : 'CPU Rival'}
                  </span>
                  <p className="text-xs font-black italic text-indigo-100 p-2 bg-indigo-950/80 rounded-xl border border-white/10">
                    {horseLastAction}
                  </p>
                </div>

                {/* Shot Type Selection Grid */}
                {horseTurn === 'player' && (
                  <div className="space-y-2">
                    <span className="text-[10px] font-black uppercase text-indigo-300 block">
                      Choose Your Shot Callout:
                    </span>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {HORSE_SHOT_TYPES.map((shot) => (
                        <button
                          key={shot.id}
                          type="button"
                          onClick={() => setHorseSelectedShot(shot)}
                          className={`p-2.5 rounded-xl text-left border transition ${
                            horseSelectedShot.id === shot.id
                              ? 'bg-lime-400 text-black font-black italic shadow-md'
                              : 'bg-indigo-950/60 text-white border-white/10 hover:bg-indigo-950'
                          }`}
                        >
                          <p className="text-xs font-bold truncate">{shot.name}</p>
                          <p className="text-[10px] opacity-80">
                            Difficulty: {shot.difficulty}% • +{shot.xp} XP
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shot Meter Bar */}
                {horseTurn === 'player' && (
                  <div className="space-y-2 max-w-md mx-auto pt-2">
                    <div className="relative w-full h-7 bg-indigo-950 rounded-2xl overflow-hidden border-2 border-white/20 p-1">
                      <div className="absolute top-1 bottom-1 left-[44%] right-[44%] bg-lime-400 rounded-lg opacity-80 animate-pulse border border-white" />
                      <div
                        className="absolute top-0 bottom-0 w-2.5 bg-rose-500 rounded-full shadow-lg shadow-rose-500 transition-all duration-75"
                        style={{ left: `calc(${atwMeterPosition}% - 5px)` }}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleHorsePlayerShoot}
                      className="w-full py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-xs uppercase rounded-xl shadow-lg shadow-lime-400/20 transition"
                    >
                      {atwIsMeterActive ? 'RELEASE SHOT NOW!' : 'START SHOT METER'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* HORSE Game Over Card */
              <div className="p-8 bg-gradient-to-br from-indigo-900 to-indigo-950 border-2 border-lime-400 rounded-3xl text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-lime-400 text-black rounded-3xl flex items-center justify-center font-black text-3xl shadow-xl">
                  {horseWinner === 'player' ? '👑' : '💔'}
                </div>
                <div>
                  <h4 className="text-2xl font-black italic uppercase text-white">
                    {horseWinner === 'player' ? 'H-O-R-S-E VICTORY!' : 'CPU Rival Won'}
                  </h4>
                  <p className="text-xs text-indigo-200 mt-1">
                    {horseWinner === 'player'
                      ? 'You forced H-O-R-S-E on the CPU Rival! Earned +200 XP!'
                      : 'Good attempt! Try again to refine your shot meter timing.'}
                  </p>
                </div>

                <button
                  onClick={handleResetHorse}
                  className="px-6 py-3 bg-lime-400 text-black font-black italic text-xs uppercase rounded-xl shadow-lg"
                >
                  Rematch H-O-R-S-E 🔄
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
